import React, { useState } from "react"
import Config from "react-global-configuration"
import { Container, makeStyles, Box, Typography, TextField, Button } from "@material-ui/core"
import { useParams, NavLink } from "react-router-dom"
import { LogoFull, LoadingSpinner, PaperBox, PinInput } from "../../components"
import { useQueryVerifyInvite, useMutationActivateSelf } from "../../data"
import { useFormUtils } from "../../utils"
import { PublicContainer } from "../Master"
import { PasswordInput } from "../../components/PasswordInput/PasswordInput"

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 300,
    padding: 0,
  },
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  logo: {
    marginBottom: theme.spacing(5),
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    textAlign: "center",
  },
  pin: {
    display: "flex",
    justifySelf: "center",
  },
  submit: {
    margin: theme.spacing(2, 0, 2),
  },
}))

const Invite = () => {
  const classes = useStyles()
  const { pinLength } = Config.get("auth")
  const { isValid } = useFormUtils()
  const { id, token } = useParams()
  const [activateSelf, { loading: activateSelfLoading }] = useMutationActivateSelf()
  const { loading, data, error } = useQueryVerifyInvite({ variables: { id, token } })

  const [activated, setActivated] = useState(false)
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [pin, setPin] = useState("")

  const handlePasswordChange = (value) => {
    setPassword(value)
  }

  const handleSubmit = async () => {
    if (formValid()) {
      await activateSelf({
        variables: {
          id,
          token,
          password,
          pin,
        },
      })
      setActivated(true)
    }
  }

  const formValid = () =>
    isValid(password, confirmPassword, pin) && password === confirmPassword && pin.length === pinLength

  const isFormValid = formValid()

  return (
    <PublicContainer>
      <PaperBox p={5}>
        <Container component="main" className={classes.root}>
          <div className={classes.wrapper}>
            <Box mb={2}>
              <LogoFull width="197" />
            </Box>
            {loading && (
              <>
                <LoadingSpinner size={60} />
              </>
            )}
            {data && (
              <>
                <Typography paragraph align="center">
                  <strong>Welcome {data.verifyInvite.firstName}</strong>!
                </Typography>

                {!activated && (
                  <>
                    <Typography align="center">
                      To activate your account, please choose and confirm a password.
                    </Typography>
                    <Typography align="center" variant="caption" />

                    <Box mb={3}>
                      <PasswordInput
                        required
                        fullWidth
                        label="Password"
                        type="password"
                        value={password}
                        onChange={handlePasswordChange}
                        disabled={loading || activateSelfLoading}
                      />

                      <TextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        label="Confirm password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading || activateSelfLoading}
                      />
                    </Box>

                    <Typography align="center">
                      Now choose a unique 4-digit PIN so you can log into Operandio tablet apps.
                    </Typography>

                    <Box mt={2} mb={3}>
                      <PinInput
                        alignItems="center"
                        length={pinLength}
                        onChange={setPin}
                        className={classes.pin}
                        password
                        margin="normal"
                        allowShow
                        showLoading={false}
                        disabled={loading || activateSelfLoading}
                      />
                    </Box>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      className={classes.submit}
                      onClick={handleSubmit}
                      size="large"
                      disabled={loading || activateSelfLoading || !isFormValid}
                    >
                      {activateSelfLoading && <LoadingSpinner size="30px" delay={false} />}
                      {!activateSelfLoading && <>Activate my account</>}
                    </Button>
                  </>
                )}
                {activated && (
                  <>
                    <Typography paragraph align="center">
                      Your account has been activated and you can now sign in.
                    </Typography>
                    <Button variant="contained" component={NavLink} to="/">
                      Proceed to sign in
                    </Button>
                  </>
                )}
              </>
            )}
            {error && (
              <>
                <Typography paragraph align="center">
                  <strong>{error.graphQLErrors && error.graphQLErrors.map((err) => err.message).join(",")}</strong>
                </Typography>
                <Typography align="center">
                  Please try again. If the problem persists contact your system administrator and request they send a
                  new invite.
                </Typography>
              </>
            )}
          </div>
        </Container>
      </PaperBox>
    </PublicContainer>
  )
}

export { Invite }
